import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Divider, Input, message, Row, Space, Spin, Steps, Typography } from "antd";
import { CheckCircleOutlined, KeyOutlined, QrcodeOutlined, SafetyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { auth, firestore as db } from "configs/FirebaseConfig";
import QRCode from "qrcode";
import { useAuth } from "../../data/hooks/firebase/useAuth";
import ReauthenticationModal from "./ReauthenticationModal";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const TotpSetup = ({ isEnabled, isRequired, onSetupComplete }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const { uid: authUserId } = useAuth();

  // Check if MFA is required from navigation state
  const mfaRequiredFromState = location.state?.mfaRequired ?? isRequired ?? false;

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [totpSecret, setTotpSecret] = useState(null);

  // Reauthentication state
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [pendingOperation, setPendingOperation] = useState(null);

  // Generate QR code when qrCodeUrl changes or step changes
  useEffect(() => {
    if (qrCodeUrl && currentStep === 1 && canvasRef.current) {
      // Small delay to ensure canvas is rendered
      setTimeout(() => {
        if (canvasRef.current) {
          QRCode.toCanvas(
            canvasRef.current,
            qrCodeUrl,
            {
              width: 200,
              margin: 2,
              color: {
                dark: "#000000",
                light: "#FFFFFF",
              },
            },
            (error) => {
              if (error) {
                console.error("Error generating QR code:", error);
              }
            },
          );
        }
      }, 100);
    }
  }, [qrCodeUrl, currentStep]);

  // Handle reauthentication success
  const handleReauthSuccess = async () => {
    setShowReauthModal(false);

    if (pendingOperation === "startEnrollment") {
      await performStartEnrollment();
    }

    setPendingOperation(null);
  };

  // Handle reauthentication cancel
  const handleReauthCancel = () => {
    setShowReauthModal(false);
    setPendingOperation(null);
    setEnrolling(false);
    setLoading(false);
  };

  // Perform the actual TOTP enrollment start (after reauthentication if needed)
  const performStartEnrollment = async () => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("No authenticated user");
    }

    // Get multi-factor session
    const multiFactorSession = await multiFactor(user).getSession();

    // Start TOTP enrollment - this should provide the secret
    const totpEnrollment = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
    setTotpSecret(totpEnrollment);

    // Generate QR code URL
    const qrUrl = totpEnrollment.generateQrCodeUrl(user.email, "Chef's Farm CMS");

    setQrCodeUrl(qrUrl);
    setSecret(totpEnrollment.secretKey);
    setCurrentStep(1);
  };

  // Start TOTP enrollment process (with reauthentication check)
  const startEnrollment = async () => {
    try {
      setLoading(true);
      setEnrolling(true);
      await performStartEnrollment();
    } catch (error) {
      console.error("Error starting TOTP enrollment:", error);

      // Check if reauthentication is required
      if (error.code === "auth/requires-recent-login") {
        setPendingOperation("startEnrollment");
        setShowReauthModal(true);
      } else {
        message.error(t("notifications.totp_setup_error"));
        setEnrolling(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Complete TOTP enrollment
  const completeEnrollment = async () => {
    try {
      setLoading(true);

      const user = auth.currentUser;

      if (!user || !totpSecret) {
        throw new Error("Missing user or TOTP secret");
      }

      // Get multi-factor session
      const multiFactorSession = await multiFactor(user).getSession();

      // Create TOTP assertion with the verification code
      const totpAssertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);

      // Enroll the TOTP factor with a display name
      await multiFactor(user).enroll(totpAssertion, "Chef's Farm CMS TOTP");

      // Update user profile in Firestore
      await updateDoc(doc(db, "users", authUserId), {
        totpEnabled: true,
        totpSetupDate: dayjs().unix(),
      });

      // Generate backup codes (simulate - in real app you'd generate these properly)
      const codes = generateBackupCodes();
      setBackupCodes(codes);

      setCurrentStep(2);
      message.success(t("notifications.totp_setup_complete"));
    } catch (error) {
      console.error("Error completing TOTP enrollment:", error);

      // Handle specific Firebase auth errors
      if (error.code === "auth/code-expired") {
        message.error(t("notifications.totp_code_expired"));
        // Reset to step 0 to restart the process
        setCurrentStep(0);
        setEnrolling(false);
        setTotpSecret(null);
        setQrCodeUrl("");
        setSecret("");
        setVerificationCode("");
      } else if (error.code === "auth/invalid-verification-code") {
        message.error(t("notifications.totp_invalid_code"));
      } else {
        message.error(t("notifications.totp_verification_failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate backup codes (simulate)
  const generateBackupCodes = () => {
    const codes = [];
    for (let i = 0; i < 8; i++) {
      codes.push(Math.random().toString(36).substring(2, 10).toUpperCase());
    }
    return codes;
  };

  // Copy backup codes to clipboard
  const copyBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    navigator.clipboard.writeText(codesText);
    message.success(t("notifications.backup_codes_copied"));
  };

  // Copy secret to clipboard
  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    message.success(t("notifications.secret_copied"));
  };

  const finishSetup = () => {
    setEnrolling(false);
    setCurrentStep(0);

    if (onSetupComplete) {
      onSetupComplete();
    }

    // If MFA was required, navigate to app after setup
    if (mfaRequiredFromState) {
      message.success(t("notifications.mfa_setup_complete_redirecting"));
      setTimeout(() => {
        navigate(APP_PREFIX_PATH);
      }, 1500);
    }
  };

  // If MFA is required but not enabled, show forced setup flow
  if (mfaRequiredFromState && !isEnabled) {
    return (
      <>
        {/* Reauthentication Modal */}
        <ReauthenticationModal
          visible={showReauthModal}
          onSuccess={handleReauthSuccess}
          onCancel={handleReauthCancel}
          operationName={t("setup_totp")}
        />

        <Card>
          {!enrolling && (
            <Alert
              message={t("mfa_required_title")}
              description={t("mfa_required_description")}
              type="warning"
              showIcon
              style={{ marginBottom: "24px" }}
              icon={<SafetyOutlined />}
            />
          )}

          {enrolling ? (
            <>
              <Steps current={currentStep} style={{ marginBottom: "24px" }}>
                <Step title={t("scan_qr_code")} icon={<QrcodeOutlined />} />
                <Step title={t("verify_code")} icon={<SafetyOutlined />} />
                <Step title={t("backup_codes")} icon={<CheckCircleOutlined />} />
              </Steps>

              {currentStep === 0 && (
                <div style={{ textAlign: "center" }}>
                  <Spin size="large" />
                  <div style={{ marginTop: "16px" }}>
                    <Text>{t("generating_qr_code")}</Text>
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <div>
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={12}>
                      <div style={{ textAlign: "center" }}>
                        <Title level={4}>{t("scan_qr_code")}</Title>
                        <div
                          style={{
                            padding: "20px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                            marginBottom: "16px",
                          }}
                        >
                          {qrCodeUrl ? (
                            <canvas
                              ref={canvasRef}
                              style={{
                                maxWidth: "200px",
                                maxHeight: "200px",
                                border: "1px solid #ddd",
                              }}
                            />
                          ) : (
                            <QrcodeOutlined style={{ fontSize: "100px", color: "#ccc" }} />
                          )}
                        </div>
                        <Text type="secondary">{t("scan_qr_description")}</Text>
                      </div>
                    </Col>

                    <Col xs={24} md={12}>
                      <Title level={4}>{t("manual_entry")}</Title>
                      <Paragraph>{t("manual_entry_description")}</Paragraph>
                      <div
                        style={{
                          padding: "12px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "4px",
                          marginBottom: "16px",
                          fontFamily: "monospace",
                        }}
                      >
                        <Text copyable={{ onCopy: copySecret }}>{secret}</Text>
                      </div>

                      <Divider />

                      <Title level={4}>{t("verify_setup")}</Title>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Text>{t("enter_verification_code")}</Text>
                        <Input
                          placeholder={t("enter_6_digit_code")}
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          maxLength={6}
                          style={{ width: "150px" }}
                        />
                        <Button
                          type="primary"
                          onClick={completeEnrollment}
                          loading={loading}
                          disabled={verificationCode.length !== 6}
                        >
                          {t("verify_and_enable")}
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </div>
              )}

              {currentStep === 2 && (
                <div>
                  <Alert
                    message={t("totp_setup_success")}
                    description={t("totp_setup_success_description")}
                    type="success"
                    showIcon
                    style={{ marginBottom: "24px" }}
                  />

                  <Space>
                    <Button type="primary" onClick={finishSetup}>
                      {t("finish_setup")}
                    </Button>
                  </Space>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <KeyOutlined style={{ fontSize: "48px", color: "#fa8c16", marginBottom: "16px" }} />
              <Title level={4}>{t("mfa_setup_required_title")}</Title>
              <Paragraph type="secondary">{t("mfa_setup_required_subtitle")}</Paragraph>
              <Button
                style={{ marginTop: "16px" }}
                type="primary"
                icon={<SafetyOutlined />}
                onClick={startEnrollment}
                loading={loading}
                size="large"
              >
                {t("setup_mfa_now")}
              </Button>
            </div>
          )}
        </Card>
      </>
    );
  }

  // Regular optional MFA setup flow
  if (enrolling) {
    return (
      <>
        {/* Reauthentication Modal */}
        <ReauthenticationModal
          visible={showReauthModal}
          onSuccess={handleReauthSuccess}
          onCancel={handleReauthCancel}
          operationName={t("setup_totp")}
        />

        <Card>
          <Steps current={currentStep} style={{ marginBottom: "24px" }}>
            <Step title={t("scan_qr_code")} icon={<QrcodeOutlined />} />
            <Step title={t("verify_code")} icon={<SafetyOutlined />} />
            <Step title={t("backup_codes")} icon={<CheckCircleOutlined />} />
          </Steps>

          {currentStep === 0 && (
            <div style={{ textAlign: "center" }}>
              <Spin size="large" />
              <div style={{ marginTop: "16px" }}>
                <Text>{t("generating_qr_code")}</Text>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <div style={{ textAlign: "center" }}>
                    <Title level={4}>{t("scan_qr_code")}</Title>
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        marginBottom: "16px",
                      }}
                    >
                      {qrCodeUrl ? (
                        <canvas
                          ref={canvasRef}
                          style={{
                            maxWidth: "200px",
                            maxHeight: "200px",
                            border: "1px solid #ddd",
                          }}
                        />
                      ) : (
                        <QrcodeOutlined style={{ fontSize: "100px", color: "#ccc" }} />
                      )}
                    </div>
                    <Text type="secondary">{t("scan_qr_description")}</Text>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <Title level={4}>{t("manual_entry")}</Title>
                  <Paragraph>{t("manual_entry_description")}</Paragraph>
                  <div
                    style={{
                      padding: "12px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      marginBottom: "16px",
                      fontFamily: "monospace",
                    }}
                  >
                    <Text copyable={{ onCopy: copySecret }}>{secret}</Text>
                  </div>

                  <Divider />

                  <Title level={4}>{t("verify_setup")}</Title>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text>{t("enter_verification_code")}</Text>
                    <Input
                      placeholder={t("enter_6_digit_code")}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      maxLength={6}
                      style={{ width: "150px" }}
                    />
                    <Button
                      type="primary"
                      onClick={completeEnrollment}
                      loading={loading}
                      disabled={verificationCode.length !== 6}
                    >
                      {t("verify_and_enable")}
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Alert
                message={t("totp_setup_success")}
                description={t("totp_setup_success_description")}
                type="success"
                showIcon
                style={{ marginBottom: "24px" }}
              />

              <Space>
                <Button type="primary" onClick={finishSetup}>
                  {t("finish_setup")}
                </Button>
              </Space>
            </div>
          )}
        </Card>
      </>
    );
  }

  return (
    <div>
      {isEnabled ? (
        <Card>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CheckCircleOutlined style={{ fontSize: "48px", color: "#52c41a", marginBottom: "16px" }} />
            <Title level={4}>{t("totp_enabled")}</Title>
            <Paragraph type="secondary">{t("totp_enabled_description")}</Paragraph>
          </div>
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <KeyOutlined style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }} />
            <Title level={4}>{t("totp_not_enabled")}</Title>
            <Paragraph type="secondary">{t("totp_not_enabled_description")}</Paragraph>
            <Button
              style={{ marginTop: "16px" }}
              type="primary"
              icon={<SafetyOutlined />}
              onClick={startEnrollment}
              loading={loading}
            >
              {t("setup_totp")}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default TotpSetup;
